import { VOLATILITY_MAX } from "./volatility.js";

/**
 * The filters of the boxes lists (the store's own boxes and the Forge's, under the arcade
 * layouts): the price ranges and each list's sorts. Data only; their words are the default copy's
 * (`storefrontCopy/shared.ts`, `defaultBoxListCopy`).
 */

/** The price ranges a list offers, the whole range first. `afford` = what the balance covers. */
export const BOX_PRICE_RANGES = [
  "all",
  "afford",
  "under-10",
  "10-25",
  "25-50",
  "50-100",
  "100-up",
] as const;
export type BoxPriceRange = (typeof BOX_PRICE_RANGES)[number];

/**
 * Each range's bounds in cents: `min` included, `max` left out (the next range starts there).
 * `all` and `afford` have none; `afford` is capped by the shopper's spendable balance instead.
 */
export const BOX_PRICE_RANGE_BOUNDS: Record<BoxPriceRange, { min?: number; max?: number }> = {
  all: {},
  afford: {},
  "under-10": { max: 1_000 },
  "10-25": { min: 1_000, max: 2_500 },
  "25-50": { min: 2_500, max: 5_000 },
  "50-100": { min: 5_000, max: 10_000 },
  "100-up": { min: 10_000 },
};

/** Whether a price in cents is in a range; `afford` passes what `spendableCents` covers. */
export function isInBoxPriceRange(
  priceCents: number,
  range: BoxPriceRange,
  spendableCents: number | null,
): boolean {
  if (range === "afford") return spendableCents !== null && priceCents <= spendableCents;
  const { min, max } = BOX_PRICE_RANGE_BOUNDS[range];
  return (min === undefined || priceCents >= min) && (max === undefined || priceCents < max);
}

/** Every sort a list may offer: by opens, by date either way, by price either way, or favorites. */
export const BOX_LIST_SORTS = [
  "popular",
  "new",
  "old",
  "price-desc",
  "price-asc",
  "favorites",
] as const;
export type BoxListSort = (typeof BOX_LIST_SORTS)[number];

/** The two lists the arcade layouts switch between: the store's boxes and the Forge's. */
export type BoxList = "official" | "community";

/**
 * A list's filters with none set, the same on both lists: every name and price, the opens order
 * and the whole volatility scale. The design system's list filters reset to it.
 */
export const BOX_LIST_DEFAULTS = {
  query: "",
  price: "all",
  sort: "popular",
  volatility: [0, VOLATILITY_MAX],
} as const satisfies {
  query: string;
  price: BoxPriceRange;
  sort: BoxListSort;
  volatility: readonly [number, number];
};

/** Each list's sorts, its default first and Favorites last. */
export const BOX_LIST_SORTS_OF: Record<BoxList, readonly BoxListSort[]> = {
  official: ["popular", "new", "price-desc", "price-asc", "favorites"],
  community: ["popular", "new", "old", "price-desc", "price-asc", "favorites"],
};

/** A count as a list's card shows it: 950, 3.4k, 1.2M. */
export function formatCompactCount(count: number): string {
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 })
    .format(count)
    .replace("K", "k");
}
